// components/accounts/RentPayments.tsx — rent received, in two steps. Site staff record a receipt against a flat
// (it waits as PENDING); Finance confirms it, which posts it to the ledger and ticks the rent roll, or rejects it.
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { api } from '../../lib/api';
import { useCan } from '../../hooks/usePermission';
import { Badge, Button, Field, Input, Modal, Select } from '../ui';
import { Confirm, FormError } from '../forms/common';
import { formatMoney } from '../../lib/viz';
import type { RentRoll } from '../../lib/fees';
type RentCell = RentRoll['rows'][number]['cells'][number];

type Payment = Record<string, any>;
export const PAYMENT_METHODS: Array<[string, string]> = [['BANK_TRANSFER', 'Bank transfer'], ['DIRECT_DEBIT', 'Direct debit'], ['CARD', 'Card'], ['CASH', 'Cash'], ['CHEQUE', 'Cheque']];
const today = () => new Date().toISOString().slice(0, 10);
/** "2026-10" → "Oct 26". */
export const monthLabel = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });

/** True when this user may log a receipt at all (as pending, or straight in if they are Finance). */
export function useCanRecordPayment() {
  const record = useCan('payments.record'); const create = useCan('payments.create'); const invoice = useCan('invoices.recordPayment');
  return record || create || invoice;
}

/** After a payment changes, every view of the property's money is stale. */
function useRefreshMoney(propertyId: string | undefined) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['/payments'] });
    if (propertyId) { void qc.invalidateQueries({ queryKey: ['property-accounts', propertyId] }); void qc.invalidateQueries({ queryKey: ['rent-roll', propertyId] }); void qc.invalidateQueries({ queryKey: ['/arrears', { propertyId }] }); void qc.invalidateQueries({ queryKey: ['/arrears/summary', propertyId] }); }
  };
}

/**
 * "Record payment" for one flat. Tick the months it covers (owing months are ticked to start; months not yet due can be
 * ticked to record rent paid ahead), then when and how it arrived. Saves as PENDING for Finance to confirm.
 */
export function RecordPaymentModal({ propertyId, entityId, currency, row, onClose }: {
  propertyId: string; entityId: string; currency: string;
  row: { tenancyId: string; unit: string | null; tenants: string | null; owingMinor: number; rentMinor: number; cells: RentCell[] };
  onClose: () => void;
}) {
  const canCreate = useCan('payments.create'); const canInvoice = useCan('invoices.recordPayment');
  const finance = canCreate || canInvoice;
  const refresh = useRefreshMoney(propertyId);
  // Months with something still to pay: owing ones first (fallen due), then the ones not due yet.
  const open = useMemo(() => row.cells.filter((c) => c.dueMinor > c.paidMinor).map((c) => ({ month: c.month, left: c.dueMinor - c.paidMinor, due: c.state !== 'DUE' })), [row.cells]);
  const [ticked, setTicked] = useState<Set<string>>(() => new Set(open.filter((c) => c.due).map((c) => c.month)));
  const sumTicked = open.filter((c) => ticked.has(c.month)).reduce((s, c) => s + c.left, 0);
  const [form, setForm] = useState({ amount: ((sumTicked || row.owingMinor || row.rentMinor) / 100).toFixed(2), receivedOn: today(), method: 'BANK_TRANSFER', reference: '' });
  const set = (p: Partial<typeof form>) => setForm((f) => ({ ...f, ...p }));
  const toggle = (m: string) => setTicked((cur) => {
    const n = new Set(cur); if (n.has(m)) n.delete(m); else n.add(m);
    const total = open.filter((c) => n.has(c.month)).reduce((s, c) => s + c.left, 0);
    set({ amount: (total / 100).toFixed(2) });
    return n;
  });
  const amountMinor = Math.round(Number(form.amount) * 100);
  const save = useMutation({
    mutationFn: async () => (await api.post<Payment>('/payments', {
      entityId, tenancyId: row.tenancyId, amountMinor, currency, receivedOn: form.receivedOn, method: form.method,
      externalReference: form.reference.trim() || null, status: 'PENDING',
    })).data,
    onSuccess: () => { refresh(); onClose(); },
  });
  return (
    <Modal open onClose={onClose} title={`Record payment · ${row.unit ?? ''}`}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => save.mutate()} disabled={!(amountMinor > 0) || !form.receivedOn || save.isPending}>{save.isPending ? 'Saving…' : 'Record payment'}</Button></>}>
      <div className="space-y-3">
        <p className="text-[12.5px] text-ink-600">{row.tenants ?? 'Tenant'}{row.owingMinor ? <> · owing <b>{formatMoney(row.owingMinor, currency)}</b></> : ' · nothing owing'}</p>
        {open.length > 0 && (
          <Field label="Months this covers" hint="Rent not yet due can be ticked to record it paid ahead. The ledger applies money to the oldest unpaid month first.">
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
              {open.map((c) => {
                const on = ticked.has(c.month);
                return (
                  <button key={c.month} type="button" role="checkbox" aria-checked={on} onClick={() => toggle(c.month)}
                    className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-[12.5px] transition ${on ? 'border-bronze-500 bg-bronze-50 text-bronze-700' : 'border-ink-200 bg-white text-ink-700 hover:border-ink-300'}`}>
                    <span>{monthLabel(c.month)}<span className={`block text-[10.5px] ${on ? 'text-bronze-600' : c.due ? 'text-red-600' : 'text-ink-400'}`}>{c.due ? 'owing' : 'not due yet'}</span></span>
                    <span className="tabular-nums">{formatMoney(c.left, currency)}</span>
                  </button>
                );
              })}
            </div>
          </Field>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={`Amount (${currency})`}><Input value={form.amount} onChange={(e) => set({ amount: e.target.value })} inputMode="decimal" className="w-full" /></Field>
          <Field label="Received on"><Input type="date" value={form.receivedOn} onChange={(e) => set({ receivedOn: e.target.value })} className="w-full" /></Field>
          <Field label="How"><Select value={form.method} onChange={(e) => set({ method: e.target.value })} className="w-full">{PAYMENT_METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
          <Field label="Reference" hint="What the tenant quoted, or a note for Finance"><Input value={form.reference} onChange={(e) => set({ reference: e.target.value })} maxLength={80} className="w-full" placeholder="Optional" /></Field>
        </div>
        <p className="rounded-lg bg-ink-50 px-3.5 py-2.5 text-[12px] text-ink-600">
          {finance ? 'It is saved as awaiting confirmation. Confirm it on Finance → Transactions once the money is in the bank.' : 'It is saved as awaiting confirmation. Finance confirms it once the money is in the bank; the flat shows as paid after that. Only one payment per flat can wait at a time.'}
        </p>
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}

/** Confirm / reject for one pending payment. Only shown to users who may clear payments. */
export function PendingPaymentActions({ payment, propertyId, compact = false }: { payment: Payment; propertyId?: string; compact?: boolean }) {
  const canClear = useCan('payments.update');
  const refresh = useRefreshMoney(propertyId ?? payment.propertyId ?? undefined);
  const [rejecting, setRejecting] = useState(false);
  const change = useMutation({
    mutationFn: async (body: { status: 'CLEARED' | 'FAILED'; reason?: string }) => api.post(`/payments/${payment.id}/status`, body),
    onSuccess: () => { setRejecting(false); refresh(); },
  });
  if (!canClear) return <Badge value="Awaiting confirmation" tone="warning" />;
  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <Button size="sm" onClick={() => change.mutate({ status: 'CLEARED' })} disabled={change.isPending} title="The money is in the bank: post it"><Check className="h-3.5 w-3.5" />{!compact && ' Confirm'}</Button>
      <Button size="sm" variant="secondary" onClick={() => setRejecting(true)} disabled={change.isPending} title="Not received: reject it"><X className="h-3.5 w-3.5" />{!compact && ' Reject'}</Button>
      <Confirm open={rejecting} onClose={() => setRejecting(false)} title={`Reject ${payment.reference}?`} body="The payment is marked as failed and nothing is posted. The flat stays as owing." confirmLabel="Reject payment"
        reasonLabel="Why it is rejected" pending={change.isPending} error={change.error} onConfirm={(reason?: string) => change.mutate({ status: 'FAILED', reason: reason || undefined })} />
    </span>
  );
}
